// Push notification model types - converted from Go backend models

import type {
    PushNotificationID,
    UserID,
    CoreRecommendationID,
    EarningsRecommendationID,
    SwingTradeID,
    LongTermRecommendationID,
    VideoID,
    EventID,
    SubscriptionID
} from '@/models/ids';
import type { RFC3339, Timestamps } from '@/models/base-types';

export const PushNotificationStatus = {
    WAITING: 'WAITING',
    SUCCESS: 'SUCCESS',
    FAILED: 'FAILED'
} as const;

export type PushNotificationStatus = typeof PushNotificationStatus[keyof typeof PushNotificationStatus];

export interface PushNotification extends Timestamps {
    id: PushNotificationID;

    userID: UserID;          // Receiver of the push notification
    firebaseUID: string;     // Firebase registration token used to actually send

    title: string;                       // Notification title
    text: string;                        // Notification body text
    extraData: Record<string, string>;   // Arbitrary key-value data (firebase "data" field), never null
    imageURL: string | null;             // Optional image url

    status: PushNotificationStatus;

    nextTryTime: RFC3339 | null;         // When the next send-attempt should happen
    tryCount: number;                    // Number of send-attempts so far
    maxTries: number;                    // Maximum number of send-attempts

    firebaseMessageID: string | null;    // Message id returned by firebase on success

    sentTime: RFC3339 | null;            // When successfully sent
    failTime: RFC3339 | null;            // When finally failed
    failReason: string | null;           // Last failure reason
    opened: RFC3339 | null;              // When the client reported the notification as opened

    // References to the entity that triggered this notification (all nullable)
    coreRecommendationID: CoreRecommendationID | null;
    earningsRecommendationID: EarningsRecommendationID | null;
    swingTradeRecommendationID: SwingTradeID | null;
    longTermRecommendationID: LongTermRecommendationID | null;
    videoID: VideoID | null;
    eventID: EventID | null;
    subscriptionID: SubscriptionID | null;

    created: RFC3339;
    updated: RFC3339 | null;
    deleted: RFC3339 | null;
}

// List request/response types
export interface ListPushNotificationsOptions {
    status?: PushNotificationStatus;
    userID?: UserID;
    next?: string;
    limit?: number;
}

export interface ListPushNotificationsResponse {
    notifications: PushNotification[];
    next: string;
    total: number;
}

export interface SendPushNotificationRequest {
    userID: UserID;
    title: string;
    text: string;
}
